import React, { useEffect, useState, forwardRef, useImperativeHandle} from 'react';

import { Form, InputGroup } from "react-bootstrap";
import { ValidationMessages } from 'Utils/Constants';

const isEmpty = (value) => {
  return value === undefined ||
    value === null ||
    /^\s*$/.test(value);
}

/**
 *
 * @param {FormField.defaultProps} props
 * @returns
 */
const FormField = forwardRef((props, ref) => {
  const [showError, setShowError] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [fieldValue, setFieldValue] = useState(props.startValue);

  // Run validation on initial value
  useEffect(() => {
    onChange(props.startValue);
    if (isEmpty(props.startValue)) {
      // We don't want to see errors if the form hasn't been filled out.
      setShowError(false);
    }
  }, [props.startValue]);

  // Expose setFieldValue to parent via ref. Useful for causing form field updates externally.
  useImperativeHandle(ref, () => ({
    setFieldValue: (val) => {
      setFieldValue(val);
    }
  }));

  const multiValidate = (value) => {
    if (!props.validators || props.validators.length === 0) return true;
    let errors = [];
    props.validators.forEach(validator => {
      let valid = true;
      if (validator.action) {
        valid = validator.action(value);
      } else if (validator.regex) {
        valid = validator.regex.test(value);
      }
      if (!valid) {
        errors.push(validator.errorMessage ?? 'Invalid value.');
      }
    });
    setErrorMessages(errors);
    return errors.length === 0;
  };

  const isValid = (value) => {
    // Short circuit if required but empty.
    if (props.required === true && isEmpty(value)) {
      setErrorMessages([ValidationMessages.REQUIRED]);
      return false;
    }

    // Run independent validators if there's a value.
    if (!isEmpty(value))
    {
      return multiValidate(value);
    }

    // Otherwise field is optional and has no value.
    return true;
  };

  const isInvalidCharacter = (value) => {
    if (props.allowedCharacters && value.length > 0) {
      const lastChar = value[value.length - 1];
      const valid = props.allowedCharacters.test(lastChar);
      if (valid === false)
      {
        setErrorMessages([`Invalid character ${lastChar}`]);
        setShowError(true);
        return true;
      }
    }
    return false;
  }

  const onChange = (value) => {
    if (isInvalidCharacter(value)) return; // Prevent entry of disallowed characters.
    const valid = isValid(value);
    setShowError(!valid);
    setFieldValue(value);
    if (typeof props.onValueChanged === 'function') {
      props.onValueChanged(props.fieldName, value, valid);
    }
  }

  const onBlur = (val) => {
    isValid(val) ? setShowError(false) : setShowError(true);
  }

  // Don't show placeholder when disabled
  const placeholder = props.disabled ? '' : props.placeholder;
  const controlProps = { ...props, placeholder };

  return (
    <Form.Group controlId={props.fieldName}>
      <Form.Label>{props.label}</Form.Label>
      {props.renderControl(controlProps, showError, fieldValue, onChange, onBlur)}
      {showError && <div className='invalid-feedback d-block' role='alert'>
        {errorMessages.join(' ')}
      </div>}
    </Form.Group>
  );
});

const DefaultControl = (props, showError, fieldValue, onChange, onBlur) =>
<InputGroup>
    {props.prepend && <InputGroup.Prepend>
      <InputGroup.Text className={showError && 'redBorder'} id="basic-addon1">{props.prepend}</InputGroup.Text>
    </InputGroup.Prepend>}
    <Form.Control
      className={showError && 'is-invalid'}
      name={props.fieldName}
      value={fieldValue || ''}
      maxLength={props.maxLength}
      onChange={e => onChange(e.target.value)}
      onBlur={e => onBlur(e.target.value)}
      disabled={props.disabled}
      placeholder={props.placeholder}
      type={props.type}
      autoComplete={props.autoComplete}
      aria-describedby="basic-addon2"
    />
    {props.append && <InputGroup.Append>
      <InputGroup.Text className={showError && 'redBorder'} id="basic-addon2"><span dangerouslySetInnerHTML={{__html: props.append}}></span></InputGroup.Text>
    </InputGroup.Append>}
  </InputGroup>;

FormField.defaultProps = {
  fieldName: 'formField',
  label: 'Field Label',
  maxLength: 256,
  startValue: '',
  type: 'text',
  required: false, // Shortcut
  /** @type {RegExp} */
  allowedCharacters: undefined, ///[a-zA-Z0-9]/i, // Prevent typing disallowed characters
  validators: [], // See Constants.Validators
  placeholder: '',
  onValueChanged: function (fieldName, fieldValue, isValid) { console.warn('FormField onValueChanged not set.', arguments) },
  disabled: false,
  renderControl: DefaultControl,
  autoComplete: "on"
};

export default FormField;